import { useNavigate } from "react-router-dom";
import { toast } from "@volca/ui/lib/toast";
import { useCreateReport } from "./query";
import { profitabilityRunPath } from "./routes";
import type { ProfitabilityRun } from "./types";
import { reportError, runDates } from "./utils";
/**
 * Builds this report's period again. A rerun is how a report catches up with
 * jobs that closed out after it ran, so it is the same action whether the
 * report succeeded, failed, or lost its workbook.
 *
 * It creates a new run rather than replacing this one, so nothing an owner
 * already downloaded stops existing, and the new run is what the period's row
 * resolves to once it completes.
 */
export const useRerunReport = (
  businessId: string,
  /** Null while the page has no run to repeat; `rerun` then does nothing. */
  run: ProfitabilityRun | null,
) => {
  const navigate = useNavigate();
  const create = useCreateReport(businessId, run?.workflow_version ?? 2);

  return {
    rerunning: create.isPending,
    rerun: (executionMode: "replay" | "reprocess" = "reprocess") =>
      run &&
      create.mutate(
        {
          ...(runDates(run) ?? {}),
          rerun_of: run.id,
          ...(run.workflow_version === 2
            ? { execution_mode: executionMode }
            : {}),
        },
        {
          // Its own page says it is building, or why it failed; once it
          // is done, that page opens the template's page on its period.
          onSuccess: (started) => navigate(profitabilityRunPath(started.id)),
          onError: (error) =>
            toast.error("The report could not be started", {
              description: reportError(error),
            }),
        },
      ),
  };
};
